import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useInitiateStkPush, usePollMpesaStatus } from "../hooks/useMpesa";
import { MPESA_STATUS_LABELS, MpesaStatus } from "../services/mpesaApi";
import { queryClient } from "@/shared/services/queryClient";
import { useConfirm } from "@/shared/contexts/confirm-context";

interface MpesaStkPushDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceNumber?: string;
  /** The order UUID required by the STK push endpoint. May be undefined while the invoice detail loads. */
  orderId?: string;
  maxAmount?: number;
  defaultPhone?: string;
  /**
   * Fired when the user closes the dialog while the payment was successful.
   * Lets a parent dialog (e.g. Record Payment) close itself so the user can't
   * fire another STK push for an already-paid order.
   */
  onPaymentSuccess?: () => void;
}

const formatMoney = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 2,
  }).format(value);

export const MpesaStkPushDialog = ({
  open,
  onOpenChange,
  invoiceNumber,
  orderId,
  maxAmount,
  defaultPhone = "",
  onPaymentSuccess,
}: MpesaStkPushDialogProps) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [amount, setAmount] = useState("");
  // ID of the initiated transaction to poll
  const [transactionId, setTransactionId] = useState<string | null>(null);

  const stkPush = useInitiateStkPush();
  const poll = usePollMpesaStatus(transactionId);
  const confirm = useConfirm();

  // The latest transaction: either polled data (if polling) or the initiation response
  const transaction = poll.data ?? stkPush.data;
  const isPending =
    stkPush.isPending ||
    (!!transactionId && poll.isFetching && transaction?.status === MpesaStatus.Pending);
  const isPolling =
    !!transactionId && transaction?.status === MpesaStatus.Pending;
  const isSuccess = !!transaction && transaction.status === MpesaStatus.Success;
  const isFailed =
    !!transaction &&
    transaction.status !== MpesaStatus.Pending &&
    transaction.status !== MpesaStatus.Success;
  const isApiError = stkPush.isError && !transactionId;

  useEffect(() => {
    if (!open) {
      stkPush.reset();
      setTransactionId(null);
      return;
    }
    setPhoneNumber(defaultPhone);
    setAmount(maxAmount ? String(maxAmount) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When polling resolves to a terminal status, invalidate payments/orders
  useEffect(() => {
    if (!transaction) return;
    if (
      transaction.status === MpesaStatus.Success ||
      (transaction.status !== MpesaStatus.Pending && transactionId)
    ) {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["payments", "order", transaction.orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", transaction.orderId] });
    }
  }, [transaction, transactionId]);

  const handleClose = () => {
    if (stkPush.isPending || isPolling) return;
    if (isSuccess) {
      onPaymentSuccess?.();
    }
    onOpenChange(false);
  };

  const handleSubmit = () => {
    if (!orderId || !phoneNumber.trim() || !amount) return;
    confirm({
      title: "Send M-Pesa prompt?",
      description: `An STK push will be sent to ${phoneNumber.trim()} requesting payment.`,
      confirmLabel: "Send prompt",
      body: (
        <div className="space-y-1">
          <p className="flex justify-between">
            <span className="text-muted-foreground">Amount</span>
            <span className="font-medium">{formatMoney(Number(amount))}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Phone</span>
            <span className="font-medium">{phoneNumber.trim()}</span>
          </p>
        </div>
      ),
      onConfirm: () => {
        stkPush.mutate(
          { orderId, phoneNumber: phoneNumber.trim(), amount: Number(amount) },
          {
            onSuccess: (data) => {
              // Start polling regardless of initial status
              setTransactionId(data.id);
            },
          }
        );
      },
    });
  };

  const handleReset = () => {
    stkPush.reset();
    setTransactionId(null);
    if (transactionId) {
      queryClient.removeQueries({ queryKey: ["mpesa-request", transactionId] });
    }
  };

  const failureLabel = transaction?.status
    ? MPESA_STATUS_LABELS[transaction.status]
    : "Failed";

  const errorMessage =
    stkPush.error instanceof Error ? stkPush.error.message : "An unexpected error occurred.";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Pay via M-Pesa</DialogTitle>
          <DialogDescription>
            {invoiceNumber
              ? `Send M-Pesa STK push for ${invoiceNumber}.`
              : "Send an M-Pesa payment request to the customer's phone."}
          </DialogDescription>
        </DialogHeader>

        {/* Waiting / polling state */}
        {(stkPush.isPending || isPolling) && (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-center text-sm font-medium">
              M-Pesa prompt sent to {phoneNumber}
            </p>
            <p className="text-center text-xs text-muted-foreground">
              Waiting for the customer to complete payment on their phone. This may take up to a
              minute.
            </p>
          </div>
        )}

        {/* Success state */}
        {isSuccess && (
          <div className="flex flex-col items-center gap-3 py-8">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
            <p className="text-center text-sm font-medium">Payment confirmed!</p>
            <p className="text-center text-sm text-muted-foreground">
              {formatMoney(transaction!.amount)} received from {transaction!.phoneNumber}.
            </p>
            {transaction?.resultDescription && (
              <p className="text-center text-xs text-muted-foreground">
                {transaction.resultDescription}
              </p>
            )}
          </div>
        )}

        {/* Failed / Timeout / Cancelled state */}
        {isFailed && (
          <div className="flex flex-col items-center gap-3 py-8">
            <XCircle className="h-10 w-10 text-red-500" />
            <p className="text-center text-sm font-medium">{failureLabel}</p>
            {transaction?.resultDescription && (
              <p className="text-center text-xs text-muted-foreground">
                {transaction.resultDescription}
              </p>
            )}
          </div>
        )}

        {/* Network / API error state */}
        {isApiError && (
          <div className="flex flex-col items-center gap-3 py-8">
            <XCircle className="h-10 w-10 text-red-500" />
            <p className="text-center text-sm font-medium">Failed to initiate M-Pesa payment</p>
            <p className="text-center text-xs text-muted-foreground">{errorMessage}</p>
          </div>
        )}

        {/* Idle form */}
        {!stkPush.isPending && !isPolling && !stkPush.isSuccess && !transactionId && !stkPush.isError && (
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="mpesa-phone">Phone Number to Charge</Label>
              <Input
                id="mpesa-phone"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="e.g. 0712345678"
              />
              <p className="text-xs text-muted-foreground">
                Pre-filled from the customer record. You can change this to any number.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mpesa-amount">Amount (KES)</Label>
              <Input
                id="mpesa-amount"
                type="number"
                min={1}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {isSuccess || isFailed || isApiError ? (
            <>
              {(isFailed || isApiError) && (
                <Button variant="outline" onClick={handleReset}>
                  Try Again
                </Button>
              )}
              <Button onClick={handleClose}>Close</Button>
            </>
          ) : stkPush.isPending || isPolling ? null : (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!orderId || !phoneNumber.trim() || !amount}
              >
                {!orderId ? "Loading..." : "Send M-Pesa Prompt"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

